import { useRef, useState } from "react";
import { Loader2, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { logoFull, logoIcon, refreshSiteContent, useSiteContent } from "../../lib/site";
import { Button } from "../ui/button";
import ConfirmDialog from "../ConfirmDialog";

export default function LogoTab() {
    const content = useSiteContent();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const inputRef = useRef(null);
    const custom = !!content.site_logo_url;

    const pick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Dosya 8 MB sınırını aşıyor");
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const upload = async () => {
        if (!file || uploading) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/admin/logo", fd);
            await refreshSiteContent();
            setFile(null);
            setPreview(null);
            if (inputRef.current) inputRef.current.value = "";
            toast.success("Logo güncellendi — tüm sayfalarda aktif");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Logo yüklenemedi");
        } finally {
            setUploading(false);
        }
    };

    const reset = async () => {
        setConfirmReset(false);
        try {
            await api.delete("/admin/logo");
            await refreshSiteContent();
            toast.success("Varsayılan logoya dönüldü");
        } catch {
            toast.error("İşlem başarısız");
        }
    };

    return (
        <div className="space-y-6" data-testid="logo-tab">
            <div>
                <h2 className="font-display text-xl font-bold">Site Logosu</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    Yüklediğiniz logo ana sayfa, panel ve tarayıcı sekmesinde kullanılır. Beyaz zemin otomatik olarak şeffaflaştırılır; şeffaf PNG önerilir.
                </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <div className="rounded-2xl border bg-card p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Mevcut Logo {custom ? "(Özel)" : "(Varsayılan)"}
                    </p>
                    <div className="mt-4 flex h-28 items-center justify-center rounded-xl bg-stone-950 p-4">
                        <img src={logoFull(content)} alt="Tam logo" className="max-h-full max-w-full object-contain" data-testid="logo-current-full" />
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border bg-white p-1.5">
                            <img src={logoIcon(content)} alt="Logo simgesi" className="max-h-full max-w-full object-contain" data-testid="logo-current-icon" />
                        </div>
                        <p className="text-xs text-muted-foreground">Simge: menü, tarayıcı sekmesi (favicon) ve yükleme ekranlarında kullanılır.</p>
                    </div>
                </div>

                <div className="rounded-2xl border bg-card p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yeni Logo Yükle</p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={pick}
                        className="mt-4 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-terra-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-terra-700"
                        data-testid="logo-file-input"
                    />
                    {preview && (
                        <div className="mt-4 flex h-28 items-center justify-center rounded-xl bg-stone-950 p-4">
                            <img src={preview} alt="Yeni logo önizleme" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                        </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button onClick={upload} disabled={!file || uploading} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="logo-upload-button">
                            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                            Yükle ve Uygula
                        </Button>
                        {custom && (
                            <Button variant="outline" onClick={() => setConfirmReset(true)} data-testid="logo-reset-button">
                                <RotateCcw className="mr-2 h-4 w-4" /> Varsayılana Dön
                            </Button>
                        )}
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">Not: PWA uygulama simgeleri cihaza kuruluysa, yeniden kurulumda güncellenir.</p>
                </div>
            </div>

            <ConfirmDialog
                open={confirmReset}
                onClose={() => setConfirmReset(false)}
                onConfirm={reset}
                title="Varsayılan logoya dönülsün mü?"
                description="Özel logo kaldırılır; site varsayılan ATA Angora logosunu kullanır. Dilediğinizde yeniden yükleyebilirsiniz."
                confirmText="Evet, Varsayılana Dön"
                destructive
                testId="logo-reset-dialog"
            />
        </div>
    );
}
